import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditActionLabel, formatDateTime, type Paginated } from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Card, Input, Select, Skeleton, Table, Td, Th, Tr } from '@/components/ui';
import { EmptyState, FilterField, PageHeader, Pagination } from '@/components/domain';
import type { AuditItem, AuditUserOption } from '@/types';

const LIMIT = 50;

/**
 * F1.19 - audit-log. PRD par. 3.3: soeg paa bruger, handling, entitet og dato.
 *
 * Synligheden afgoeres af serveren, ikke her: admin ser alt, Finance og Saelger kun egne
 * handlinger (`audit.view_all` / `audit.view`). Brugerfilteret vises derfor kun for admin - for
 * de oevrige ville det kun kunne vaelge dem selv. Systemhandlinger har ingen bruger og vises
 * som "System" med deres agent-praefiks i handlingen.
 */
export function AdminAuditPage() {
  const { can } = useAuth();
  const isAdmin = can('audit.view_all');

  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  /** Nulstiller siden: et filter der aendres mens man staar paa side 3 ville vise tomt. */
  const filter = <T,>(set: (value: T) => void) => {
    return (value: T): void => {
      set(value);
      setOffset(0);
    };
  };

  const { data: users } = useQuery({
    queryKey: ['admin', 'audit', 'users'],
    queryFn: () => api.get<{ data: AuditUserOption[] }>('/audit/users'),
    enabled: isAdmin,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'audit', { action, entityType, userId, from, to, offset }],
    queryFn: () =>
      api.get<Paginated<AuditItem>>('/audit', {
        action: action || undefined,
        entity_type: entityType || undefined,
        user_id: userId || undefined,
        from: from ? `${from}T00:00:00.000Z` : undefined,
        // Til og med hele slutdagen - ellers ville "til i dag" udelade alt fra i dag.
        to: to ? `${to}T23:59:59.999Z` : undefined,
        limit: LIMIT,
        offset,
      }),
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description={
          isAdmin
            ? 'Hver mutation gemmes med bruger, tidspunkt og før/efter-tilstand. Poster slettes kun ' +
              'af reglen "Oprydning i audit-loggen", efter fristen for deres entitet.'
            : 'Dine egne handlinger, med tidspunkt og før/efter-tilstand.'
        }
      />

      <Card>
        <div className="grid gap-3 border-b border-line p-3 sm:grid-cols-2 lg:grid-cols-5">
          {isAdmin ? (
            <FilterField label="Bruger">
              <Select value={userId} onChange={(event) => filter(setUserId)(event.target.value)}>
                <option value="">Alle brugere</option>
                {users?.data.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </FilterField>
          ) : null}
          <FilterField label="Handling">
            <Input
              placeholder="fx approve_invoice"
              value={action}
              onChange={(event) => filter(setAction)(event.target.value)}
            />
          </FilterField>
          <FilterField label="Entitet">
            <Input
              placeholder="fx invoice"
              value={entityType}
              onChange={(event) => filter(setEntityType)(event.target.value)}
            />
          </FilterField>
          <FilterField label="Fra dato">
            <Input
              type="date"
              value={from}
              onChange={(event) => filter(setFrom)(event.target.value)}
            />
          </FilterField>
          <FilterField label="Til dato">
            <Input type="date" value={to} onChange={(event) => filter(setTo)(event.target.value)} />
          </FilterField>
        </div>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : error ? (
          <Alert tone="error" className="m-4">
            Kunne ikke hente audit-loggen.
          </Alert>
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState title="Ingen poster matcher filtrene" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th className="w-[150px]">Tidspunkt</Th>
                  <Th className="w-[180px]">Bruger</Th>
                  <Th>Handling</Th>
                  <Th className="w-[120px]">Entitet</Th>
                  <Th className="w-[90px]" />
                </tr>
              </thead>
              <tbody>
                {data?.data.map((entry) => (
                  <Fragment key={entry.id}>
                    <Tr>
                      <Td className="text-xs tabular">{formatDateTime(entry.created_at)}</Td>
                      <Td className="text-xs">
                        {entry.user?.name ?? <span className="text-ink-muted">System</span>}
                      </Td>
                      <Td className="text-xs">
                        {auditActionLabel(entry.action)}
                        <span className="ml-1 font-mono text-ink-muted">({entry.action})</span>
                      </Td>
                      <Td className="text-xs">{entry.entity_type}</Td>
                      <Td>
                        <button
                          type="button"
                          className="text-xs text-brand hover:underline"
                          onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                        >
                          {expanded === entry.id ? 'Skjul' : 'Detaljer'}
                        </button>
                      </Td>
                    </Tr>
                    {expanded === entry.id ? (
                      <Tr>
                        <Td colSpan={5} className="bg-canvas">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <p className="mb-1 text-xs font-semibold">Før</p>
                              <pre className="overflow-x-auto rounded bg-surface p-2 text-xs">
                                {JSON.stringify(entry.before_state, null, 2) ?? 'null'}
                              </pre>
                            </div>
                            <div>
                              <p className="mb-1 text-xs font-semibold">Efter</p>
                              <pre className="overflow-x-auto rounded bg-surface p-2 text-xs">
                                {JSON.stringify(entry.after_state, null, 2) ?? 'null'}
                              </pre>
                            </div>
                          </div>

                          <dl className="mt-3 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
                            <dt className="font-semibold">Entitets-id</dt>
                            <dd className="font-mono text-ink-muted">{entry.entity_id ?? '—'}</dd>
                            <dt className="font-semibold">IP-adresse</dt>
                            <dd className="font-mono text-ink-muted">{entry.ip_address ?? '—'}</dd>
                            <dt className="font-semibold">User agent</dt>
                            <dd className="break-all text-ink-muted">{entry.user_agent ?? '—'}</dd>
                          </dl>
                        </Td>
                      </Tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
